import { useState } from 'react';
import InputDataSelector from 'containers/pages/sessions/components/InputDataSelector';
import {
  FloatingFocusManager,
  FloatingPortal,
  useClick,
  useDismiss,
  useFloatingRootContext,
  useInteractions
} from '@floating-ui/react';
import Button from 'components/button/Button';
import { useTestDataContext } from 'contexts/TestDataContext';
import { Lateralities } from 'constants.js';
import { updateData } from 'utils/object';
import { formatValue } from 'utils/results';

const SelectableInputData = ({
  personId,
  testId,
  testItem,
  testItemId,
  laterality,
  value,
  label
}) => {
  const { formData, updateFormData } = useTestDataContext();

  const [isOpen, setIsOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [tooltip, setTooltip] = useState(null);

  const context = useFloatingRootContext({
    open: isOpen,
    onOpenChange: setIsOpen,
    elements: {
      reference: anchor,
      floating: tooltip
    }
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss
  ]);

  const onChange = selectedValue => {
    setIsOpen(false);

    const testData = {
      [testItemId]: { [laterality]: selectedValue.values }
    };

    if (selectedValue.relatedInputData?.length > 0) {
      selectedValue.relatedInputData.forEach(item => {
        testData[item.testItemId] = { [laterality]: item.values };
      });
    }

    updateFormData(updateData(formData, testData));
  };

  return (
    <>
      <AnchorButton
        setAnchor={setAnchor}
        showDecimals={testItem.showDecimals}
        testItemId={testItemId}
        value={value}
        isOpen={isOpen}
        label={label}
        {...getReferenceProps()}
      />
      {isOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={context}>
            <InputDataSelector
              personId={personId}
              testId={testId}
              testItem={testItem}
              testItemId={testItemId}
              laterality={Lateralities[laterality]}
              onChange={onChange}
              rootContext={context}
              setTooltip={setTooltip}
              {...getFloatingProps()}
            />
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};

export default SelectableInputData;

const AnchorButton = ({
  setAnchor,
  testItemId,
  showDecimals,
  label,
  value,
  isOpen,
  ...props
}) => {
  return (
    <div className="c-input__group r-list-item-data-value">
      {label && (
        <label
          htmlFor={`anchor-data-select-${testItemId}`}
          className="c-input__label"
          style={{ textAlign: 'center', marginBottom: 2 }}
        >
          {label}
        </label>
      )}

      <Button
        extraClassNames="c-button--trigger-data"
        select
        role="button"
        id={`anchor-data-select-${testItemId}`}
        data-qa={`anchor-data-select-${testItemId}`}
        ref={setAnchor}
        {...props}
        style={{
          paddingRight: 30,
          paddingLeft: 8,
          minHeight: 33,
          background: 'white',
          boxShadow: isOpen ? '0 0 10px -2px' : '',
          outline: isOpen ? '1px solid var(--color-primary)' : '',
          outlineOffset: 0,
          overflow: 'hidden'
        }}
      >
        {value ? formatValue(value, 4) : ''}
      </Button>
    </div>
  );
};
